import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Typography, Container } from "@mui/material";
import ClusterDenLogo from "../../Images/Login/ClusterDenLogo.png";
import ClusterDenLogoWhite from "../../Images/Login/ClusterDenLogoWhite.png";
import SignupHero from "../../Images/Login/SignupHeroCutout.png";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useThemeContext } from "../../contexts/ThemeContext";
import { getAuthToken } from "../../utils/getAuthToken";
import { claimPendingShopifyConnection } from "../../services/shopifyApi";
import ShopifyWorkspacePicker from "../../components/Auth/ShopifyWorkspacePicker";

// Same responsive fix as Login.jsx/Signup.jsx/Setup.jsx — see AUTH_COMPACT_HEIGHT_MQ in Login.jsx.
const AUTH_COMPACT_HEIGHT_MQ = "@media (max-height:800px)";

/**
 * Lands here (instead of directly on /signup) when a Shopify-origin install already has
 * a confirmed active App Pricing subscription (see shopifyOAuthController.js's callback
 * — redirectToWelcome). No plan picker, no billing details shown — that already happened
 * on Shopify's own hosted pricing page before the merchant ever got here.
 *
 * REDESIGN 2026-10-16: this page now also does the "is the merchant already logged in"
 * check that used to be resolved speculatively via a cookie at OAuth-install time (see
 * shopifyOAuthService.js's own comment on why that changed) — the same ordinary way
 * every other page in the app already answers it, via getAuthToken(). Logged in → skip
 * straight to the workspace picker and claim, their real plan never touched. Not logged
 * in → unchanged: Continue (into Signup.jsx, which maps the Shopify plan onto the new
 * account) or "Sign in" (existing ClusterDen account).
 */
const Welcome = () => {
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shop = searchParams.get("shop") || "";
  const storeHandle = shop.replace(/\.myshopify\.com$/, "");

  // undefined = still checking, null = not logged in, string = a valid access token.
  const [authToken, setAuthToken] = useState(undefined);
  const [claimError, setClaimError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getAuthToken();
      if (!cancelled) setAuthToken(token || null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const accentColor = mode === "dark" ? "#4FC389" : "#44B57C";

  const handleContinue = () => {
    const q = shop ? `?shop=${encodeURIComponent(shop)}` : "";
    navigate(`/signup${q}`);
  };

  // For a merchant whose existing ClusterDen session wasn't auto-detected (different
  // browser/device, expired session) — lets them attach this Shopify connection to
  // their existing account instead of creating a duplicate new one. Login renders at
  // "/" (confirmed in App.js), not "/login". Login.jsx forwards `shop` on to the
  // claim-pending endpoint after a successful login (with its own workspace picker).
  const handleLoginInstead = () => {
    const q = shop ? `?shop=${encodeURIComponent(shop)}` : "";
    navigate(`/${q}`);
  };

  // Already-logged-in path: workspace chosen, claim the pending connection and land
  // back in the app — same target the backend's own known-shop reconnect redirect
  // already uses (redirectResult(res, true) in shopifyOAuthController.js), so this
  // stays consistent with that existing behavior rather than guessing a home route.
  const handleWorkspaceConfirm = async (workspaceId) => {
    setClaimError("");
    try {
      await claimPendingShopifyConnection(shop, workspaceId, authToken);
      window.location.href = "/?shopify=connected";
    } catch (err) {
      // Server refused because Shopify has no active plan for this store (e.g. the plan page
      // was closed without choosing) — send them back through plan selection.
      if (err.response?.data?.error?.code === "NO_ACTIVE_SUBSCRIPTION") {
        window.location.href = `/shopify/connecting?shop=${encodeURIComponent(shop)}`;
        return;
      }
      setClaimError(err.response?.data?.message || err.message || "Could not connect this Shopify store.");
    }
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        overflow: "hidden",
        background: mode === "dark" ? "#121212" : "#FAFBFC",
        fontFamily: "Instrument Sans, sans-serif",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "stretch",
        maxWidth: "none",
        "@media (min-width:2200px)": { maxWidth: "1800px" },
        mx: "auto",
      }}
    >
      {/* Left branding card — same layout as Setup.jsx/Signup.jsx */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: "0 0 39%",
          flexDirection: "column",
          m: { md: 3 },
          p: { md: 5 },
          pl: { md: 5, lg: 5.5 },
          borderRadius: 5,
          overflow: "hidden",
          backgroundColor: mode === "dark" ? "#1E1E1E" : "#FAFBFC",
          border: mode === "dark" ? "none" : "2px solid rgba(0,0,0,0.1)",
          color: mode === "dark" ? "#f5f5f5" : "#000000",
          position: "relative",
          [AUTH_COMPACT_HEIGHT_MQ]: { m: 1.5, p: 3, pl: { md: 4, lg: 4.5 } },
        }}
      >
        <Box
          component="img"
          src={mode === "dark" ? ClusterDenLogoWhite : ClusterDenLogo}
          alt="ClusterDen"
          sx={{ height: 44, width: "auto", maxWidth: 200, objectFit: "contain", mb: 5, position: "relative", zIndex: 1 }}
        />
        <Typography
          component="h1"
          sx={{
            fontWeight: 500,
            fontSize: { md: "1.95rem", lg: "2.25rem" },
            lineHeight: 1.25,
            letterSpacing: "-0.015em",
            mb: 2,
            color: mode === "dark" ? "#f5f5f5" : "#000000",
            position: "relative",
            zIndex: 1,
          }}
        >
          Almost there
        </Typography>
        <Typography sx={{ fontSize: { md: "0.95rem", lg: "1.05rem" }, lineHeight: 1.6, color: "#9CA3AF", position: "relative", zIndex: 1 }}>
          Your Shopify plan is confirmed. One more step to set up your workspace.
        </Typography>
        {/* Hero art now sits BELOW the text in normal flex flow (it used to be absolutely
            positioned from the bottom, so on shorter screens the artwork overlapped the
            subtitle). flex:1 + minHeight:0 lets it take only the space the text leaves; the
            negative margins reproduce the old edge-to-edge, bottom-left placement by cancelling
            the card's own padding in each size mode. */}
        <Box
          component="img"
          src={SignupHero}
          alt=""
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            alignSelf: "stretch",
            mt: 2,
            ml: { md: -5, lg: -5.5 },
            mr: { md: -5 },
            mb: { md: -5 },
            objectFit: "contain",
            objectPosition: "left bottom",
            [AUTH_COMPACT_HEIGHT_MQ]: { ml: { md: -4, lg: -4.5 }, mr: { md: -3 }, mb: { md: -3 } },
          }}
        />
      </Box>

      {/* Right side */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "safe center",
          boxSizing: "border-box",
          padding: { xs: 2.5, sm: 3, md: 4 },
          overflowY: "auto",
          overflowX: "hidden",
          backgroundColor: mode === "dark" ? "#121212" : "#FAFBFC",
        }}
      >
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1, boxSizing: "border-box" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
              maxWidth: 480,
              boxSizing: "border-box",
              mx: "auto",
              py: { xs: 3.5, sm: 4.5, md: 5 },
              px: { xs: 3, sm: 4.5, md: 5 },
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: accentColor, mb: 2 }} />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.85rem", sm: "2.1rem" },
                lineHeight: 1.2,
                color: mode === "dark" ? "#f5f5f5" : "#121a34",
                fontFamily: "Instrument Sans, sans-serif",
                mb: 1,
              }}
            >
              Shopify subscription confirmed
            </Typography>

            {authToken ? (
              // Already logged in — skip straight to the workspace picker, no
              // Continue/Log-in-instead choice needed.
              <Box sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    fontFamily: "Instrument Sans, sans-serif",
                    mb: 3,
                  }}
                >
                  {storeHandle ? `${storeHandle} is subscribed through Shopify.` : "Subscription confirmed."} Choose
                  which workspace to connect it to.
                </Typography>
                {claimError && (
                  <Typography sx={{ color: "#ef4444", fontSize: "13px", mb: 1.5 }}>{claimError}</Typography>
                )}
                <ShopifyWorkspacePicker
                  token={authToken}
                  mode={mode}
                  onConfirm={handleWorkspaceConfirm}
                  onError={(err) =>
                    setClaimError(err.response?.data?.message || err.message || "Could not load your workspaces.")
                  }
                />
              </Box>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    fontFamily: "Instrument Sans, sans-serif",
                    mb: 4,
                  }}
                >
                  {storeHandle
                    ? `${storeHandle} is subscribed through Shopify. Continue to finish setting up your ClusterDen account.`
                    : "Continue to finish setting up your ClusterDen account."}
                </Typography>

                <Button
                  onClick={handleContinue}
                  fullWidth
                  variant="contained"
                  disabled={authToken === undefined}
                  sx={{
                    py: 1.75,
                    backgroundColor: accentColor,
                    color: "#ffffff",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "1rem",
                    letterSpacing: "0.01em",
                    fontFamily: "Instrument Sans, sans-serif",
                    boxShadow: mode === "dark" ? "none" : "0 2px 8px rgba(68,181,124,0.25)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: mode === "dark" ? "#3aad77" : "#38a06c",
                      boxShadow: mode === "dark" ? "none" : "0 8px 20px rgba(68,181,124,0.35)",
                    },
                  }}
                >
                  Continue
                </Button>

                {/* Made more prominent per earlier review — this is the one safety net for an
                    existing customer whose session wasn't auto-detected; it needs to be hard to
                    miss next to the primary Continue button, not an afterthought underneath it. */}
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "0.85rem",
                    textAlign: "center",
                    width: "100%",
                    color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    fontFamily: "Instrument Sans, sans-serif",
                  }}
                >
                  Already have an account?{" "}
                  <Button
                    onClick={handleLoginInstead}
                    sx={{
                      p: 0,
                      minWidth: "auto",
                      verticalAlign: "baseline",
                      color: mode === "dark" ? "#4FC389" : "#2D9B39",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      fontFamily: "Instrument Sans, sans-serif",
                      "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                    }}
                  >
                    Sign in
                  </Button>
                </Typography>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Welcome;
